import { useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import Container from '@mui/material/Container';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Paper from '@mui/material/Paper';
import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import Divider from '@mui/material/Divider';
import { keyframes } from '@mui/system';

// Icons
import PendingIcon from '@mui/icons-material/Pending';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import EmailIcon from '@mui/icons-material/Email';
import HomeIcon from '@mui/icons-material/Home';

const fadeIn = keyframes`
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const PendingApproval = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { profile, user } = useAuth();

  // Get status from location state or profile
  const approvalStatus = location.state?.approvalStatus || profile?.approval_status;
  const role = location.state?.role || profile?.tenant_role;
  const message = location.state?.message;

  // Redirect if already approved
  useEffect(() => {
    if (profile && approvalStatus === 'approved') {
      // User is approved, redirect to appropriate dashboard
      if (role === 'physician') {
        navigate('/clinic-dashboard', { replace: true });
      } else if (role === 'sponsor') {
        navigate('/sponsor-dashboard', { replace: true });
      } else {
        navigate('/dashboard', { replace: true });
      }
    }
  }, [profile, approvalStatus, role, navigate]);

  // Determine status and messaging
  const getStatusInfo = () => {
    if (approvalStatus === 'pending') {
      return {
        severity: 'info',
        icon: <PendingIcon sx={{ fontSize: 40 }} />,
        title: 'Application Under Review',
        message: message || 'Your application is currently under review by our team.',
        details: role === 'physician' 
          ? 'We are reviewing your medical credentials and application. You will receive an email notification once your application has been reviewed. As a platform volunteer, after approval you\'ll be able to work with one or more clinics.'
          : 'We are reviewing your sponsor application. You will receive an email notification once your application has been reviewed. As a platform volunteer, after approval you\'ll be able to work with one or more clinics.',
        timeline: 'This typically takes 2-3 business days.'
      };
    } else if (approvalStatus === 'rejected') {
      return {
        severity: 'error',
        icon: <CancelIcon sx={{ fontSize: 40 }} />,
        title: 'Application Rejected',
        message: message || 'Your application has been rejected.',
        details: 'If you believe this is an error or would like more information, please contact our support team.',
        timeline: null
      };
    } else {
      return {
        severity: 'success',
        icon: <CheckCircleIcon sx={{ fontSize: 40 }} />,
        title: 'Application Approved',
        message: 'Your application has been approved!',
        details: 'You can now access all features of the platform.',
        timeline: null
      };
    }
  };

  const statusInfo = getStatusInfo();

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        bgcolor: 'background.default',
        py: 4,
      }}
    >
      <Container maxWidth="sm">
        <Paper
          elevation={0}
          sx={{
            p: { xs: 3, sm: 4 },
            borderRadius: 2,
            animation: `${fadeIn} 0.5s ease-out`,
          }}
        >
          <Box sx={{ textAlign: 'center', mb: 3 }}>
            <Box sx={{ color: `${statusInfo.severity}.main`, mb: 2 }}>
              {statusInfo.icon}
            </Box>
            <Typography
              variant="h5"
              sx={{
                fontWeight: 600,
                mb: 2,
                color: 'text.primary'
              }}
            >
              {statusInfo.title}
            </Typography>
          </Box>

          <Alert 
            severity={statusInfo.severity} 
            sx={{ mb: 3 }}
            icon={false}
          >
            <Typography variant="body1" paragraph>
              {statusInfo.message}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {statusInfo.details}
            </Typography>
            {statusInfo.timeline && (
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1, fontStyle: 'italic' }}>
                {statusInfo.timeline}
              </Typography>
            )}
          </Alert>

          {approvalStatus === 'pending' && (
            <Box sx={{ mb: 3 }}>
              <Divider sx={{ my: 2 }} />
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                <EmailIcon color="action" />
                <Typography variant="body2" color="text.secondary">
                  Check your email ({user?.email || profile?.email}) for updates
                </Typography>
              </Box>
            </Box>
          )}

          {approvalStatus === 'rejected' && (
            <Box sx={{ mb: 3 }}>
              <Divider sx={{ my: 2 }} />
              <Typography variant="body2" color="text.secondary" paragraph>
                For questions or to appeal this decision, please contact:
              </Typography>
              <Typography variant="body2" color="primary.main">
                support@alpha01.com
              </Typography>
            </Box>
          )}

          <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', mt: 3 }}>
            <Button
              variant="outlined"
              startIcon={<HomeIcon />}
              onClick={() => navigate('/')}
            >
              Return Home
            </Button>
            {approvalStatus === 'pending' && (
              <Button
                variant="outlined"
                onClick={() => navigate('/login')}
              >
                Back to Login
              </Button>
            )}
          </Box>
        </Paper>
      </Container>
    </Box>
  );
};

export default PendingApproval;

